/**
 * `master.ts` — taksonomi **data master**: entitas, aksi, dan aturan hapus.
 *
 * ⭐ Ini ada di kontrak, bukan di `portal-api`, karena tiga lapis memakainya:
 * portal **menulis** master, outlet **menerima** turunannya (§10 master turun), klien **menampilkan**
 * namanya. Nama entitas yang menyimpang antar-lapis akan tampak sebagai "sync jalan tapi tak ada
 * yang berubah" — kegagalan paling mahal karena tak bergejala.
 *
 * @module
 */
/**
 * Entitas master yang boleh dikelola lewat API.
 *
 * ⚠️ Daftar ini **bukan** daftar seluruh tabel. `order`, `shift`, `order_event` dan kawan-kawannya
 * adalah **peristiwa dagang**, bukan master — ia tak pernah boleh disunting lewat CRUD. Batas itu
 * ditegakkan di sini supaya tak ada yang "kebetulan" menambahkan endpoint edit untuk transaksi.
 */
export const MASTER_ENTITIES = Object.freeze([
    'brand',
    'outlet',
    'outlet_settings',
    'outlet_capability',
    'area',
    'dining_table',
    'device',
    /** ⭐ Lapis DI ATAS `category` — dept → kategori → produk (Imron, 2026-08-01). */
    'department',
    'category',
    'product',
    'product_variant',
    /**
     * ⭐ **Grup harga** (Imron, 2026-08-02): "satu brand belum tentu harganya sama". Tingkat TENGAH
     * antara harga khusus outlet dan harga umum — jaringan bertarif mal/ruko/bandara tak perlu lagi
     * menyetel puluhan outlet satu per satu, dan yang terlewat saat kenaikan harga tak lagi diam.
     */
    'price_group',
    /**
     * ⭐ **Bahan** (0069) — barang yang DISIMPAN tapi tak pernah dijual.
     *
     * ⛔ Sengaja bukan `product`: `product.tax_group_id` NOT NULL (K14), dan tepung terigu tak punya
     * jawaban untuk "kena PB1 atau PPN".
     */
    'material',
    /**
     * ⭐ Blokir menu per outlet (Imron, 2026-08-02): "Alcoholic" menyala di PB-KBS, mati di PB-PKB.
     * Tabel PENGECUALIAN — bawaannya menu brand tersedia di seluruh outletnya.
     */
    'outlet_menu_block',
    /**
     * ⭐ Promo/diskon + sasaran + on/off per outlet (Imron, 2026-08-02).
     * ⛔ Voucher pihak ketiga BUKAN di sini — ia piutang (`tender_type = 'clearing'`).
     */
    'promo',
    'promo_target',
    'outlet_promo_block',
    'product_price',
    /**
     * ⭐⭐ §5e — **rute pesanan PER OUTLET** (Imron, 2026-08-02, kasus Penang Bistro).
     * "Roti Canai" keluar di KDS ASIA (PB-KBS), printer PRT_DESSERT (PB-PKB), KDS WESTERN (PB-MPP):
     * tujuan ditentukan JARAK FISIK ke stasiun, bukan jenis makanannya.
     */
    'print_station',
    'product_route',
    /** Barcode: tabel, bukan `Bar Code 1/2/3` — barcode keempat tak boleh mustahil. */
    'product_barcode',
    /**
     * §5c — combo/set menu.
     *
     * ⭐⭐ `combo_group` = **daftar opsi milik TENANT** (dipakai bersama antar set menu);
     * `combo_product_group` = grup mana yang dipakai satu set menu, **berapa** (`min`/`max`), dan
     * urutan steppernya. Dipisah 2026-08-06: di Arena 67% grup ternyata salinan isi grup lain.
     */
    'combo_group',
    'combo_product_group',
    'combo_option',
    /** §5d — modifier/condiment: grup, opsi, dan kaitannya ke produk. */
    'modifier_group',
    'modifier_option',
    'product_modifier_group',
    'payment_method',
    'tax_code',
    'outlet_tax',
    /**
     * ⭐⭐ **Keberlakuan pajak per PRODUK & per KANAL** (Imron, 2026-08-03).
     *
     * Tak semua produk kena tiap pajak: rokok & botolan tak dikenai *service charge* (tak ada jasa
     * penyajian di dalamnya) tapi tetap kena PB1; voucher & deposit tak kena dua-duanya. Dan
     * sebagian outlet membebaskan takeaway dari SC, sebagian lain tidak — Arena mengenakannya juga.
     * ⚠️ Karena itu batas kanal OPT-IN: tanpa baris pembatas, kode pajak berlaku di semua kanal.
     *
     * ⛔ Bukan `product.taxable boolean`: satu boolean tak bisa menjawab "kena PB1 tapi tidak PPN"
     * begitu pajak kedua datang (P2).
     */
    'tax_group',
    'tax_group_tax_code',
    'tax_code_channel',
    'staff_ref',
    'pos_role',
    /** §4b② — izin peran & pemetaan jabatan HR → peran POS. Inti manajemen user/role di portal. */
    'pos_role_permission',
    'position_role_map',
]);
const ENTITY_SET = new Set(MASTER_ENTITIES);
/** Guard {@link MasterEntity}. */
export function isMasterEntity(v) {
    return typeof v === 'string' && ENTITY_SET.has(v);
}
/**
 * Aksi yang tercatat di jejak master.
 *
 * ⛔ Perhatikan tak adanya `delete` di jalur normal: master **dinonaktifkan**, tidak dihapus.
 * `purge` ada hanya untuk baris yang belum pernah disentuh transaksi apa pun (salah ketik menit
 * lalu) — dan ia tetap tercatat.
 */
export const MASTER_ACTIONS = Object.freeze([
    'create',
    'update',
    'deactivate',
    'reactivate',
    'purge',
]);
const ACTION_SET = new Set(MASTER_ACTIONS);
/** Guard {@link MasterAction}. */
export function isMasterAction(v) {
    return typeof v === 'string' && ACTION_SET.has(v);
}
/**
 * ⭐ **Kolom yang TIDAK BOLEH berubah setelah baris dibuat.**
 *
 * `code` adalah nama yang dipakai integrasi, impor VTEC, struk lama, dan laporan. Mengubahnya
 * bukan mengganti label — ia **memutus rujukan yang sudah tercetak**, dan gejalanya muncul di
 * tempat yang sama sekali lain (impor gagal, laporan bolong) berbulan-bulan kemudian.
 *
 * Kalau kode memang salah: nonaktifkan yang lama, buat yang baru. Itu lebih mahal sesaat dan jauh
 * lebih murah setahun.
 */
export const IMMUTABLE_MASTER_FIELDS = Object.freeze(['id', 'code', 'created_at']);
//# sourceMappingURL=master.js.map